import React, { useEffect, useMemo, useState } from 'react';
import {
  Modal,
  Segmented,
  Input,
  Select,
  Alert,
  Space,
  Typography,
  message,
} from 'antd';
import {
  CheckCircleOutlined,
  PauseCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { API_BASE_URL } from '../../utils/config';

const { Text } = Typography;
const { TextArea } = Input;

// Red sebepleri — genel ve özel mülk için ayrı gruplar
const RED_SEBEPLERI = {
  Genel: [
    'Esnaf/Vatandaş Şikayeti',
    'Belediye Onay Alınamadı',
    'Polis Sebebi ile',
  ],
  'Özel Mülk': [
    'Esnaf/Vatandaş Şikayeti',
    'Site Yönetimi',
    'Yerin Kamu / Belediye Alanı Olması',
  ],
};

// UI aksiyonu -> insaat_talep.izin_durum değeri
const DURUM_DEGERI = {
  onay: 'onay',
  beklemede: 'beklemede',
  red: 'red',
};

/**
 * Yer Sahibi (YS) onay/red/beklemeye-al işlemleri için modal.
 * insaat_talep kaydını generic /form/submit-form endpoint'i ile günceller.
 *
 * Props:
 *  - open, onClose, onSuccess
 *  - record: { talepId, maptriksID, 'Lokasyon Adı', 'Süreç Durumu' }
 */
const YsOnayModal = ({ open, onClose, onSuccess, record }) => {
  const [aksiyon, setAksiyon] = useState('onay');
  const [aciklama, setAciklama] = useState('');
  const [mulkTipi, setMulkTipi] = useState('Genel');
  const [redSebep, setRedSebep] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setAksiyon('onay');
      setAciklama('');
      setMulkTipi('Genel');
      setRedSebep(null);
    }
  }, [open, record?.talepId]);

  const redSecenekleri = useMemo(
    () => (RED_SEBEPLERI[mulkTipi] || []).map((s) => ({ label: s, value: s })),
    [mulkTipi]
  );

  const handleSubmit = async () => {
    const talepId = record?.talepId;
    if (!talepId) {
      message.error('İnşaat talep kaydı bulunamadı (talepId yok).');
      return;
    }
    if (!aciklama || !aciklama.trim()) {
      message.warning('Açıklama girmek zorunludur.');
      return;
    }
    if (aksiyon === 'red' && !redSebep) {
      message.warning('Red durumunda bir red sebebi seçmek zorunludur.');
      return;
    }

    const formData = {
      izin_durum: DURUM_DEGERI[aksiyon],
      izin_aciklama: aciklama.trim(),
      // Red sebebi yalnızca red durumunda; diğer durumlarda temizlenir
      red_sebep: aksiyon === 'red' ? `${mulkTipi} / ${redSebep}` : null,
    };

    try {
      setSubmitting(true);
      await axios.post(
        `${API_BASE_URL}/form/submit-form`,
        {
          form_id: 0,
          table_name: 'insaat_talep',
          function: 'Update',
          unique_field: 'id',
          unique_value: talepId,
          form_data: formData,
        },
        {
          headers: {
            ...(localStorage.getItem('auth_token') && {
              Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
            }),
          },
        }
      );
      const mesaj =
        aksiyon === 'onay'
          ? 'Yer sahibi onayı kaydedildi'
          : aksiyon === 'beklemede'
          ? 'Kayıt beklemeye alındı'
          : 'Red kaydedildi';
      message.success(mesaj);
      onSuccess && onSuccess();
      onClose && onClose();
    } catch (e) {
      message.error(e?.response?.data?.detail || 'İşlem kaydedilemedi');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={`Yer Sahibi Onayı${record?.['Lokasyon Adı'] ? ` — ${record['Lokasyon Adı']}` : ''}`}
      open={open}
      onCancel={onClose}
      onOk={handleSubmit}
      okText="Kaydet"
      cancelText="Vazgeç"
      confirmLoading={submitting}
      destroyOnHidden
      width={560}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <div>
          <Text strong>Karar</Text>
          <Segmented
            block
            style={{ marginTop: 6 }}
            value={aksiyon}
            onChange={setAksiyon}
            options={[
              { label: 'Onayla', value: 'onay', icon: <CheckCircleOutlined /> },
              { label: 'Beklemeye Al', value: 'beklemede', icon: <PauseCircleOutlined /> },
              { label: 'Reddet', value: 'red', icon: <CloseCircleOutlined /> },
            ]}
          />
        </div>

        {aksiyon === 'red' && (
          <>
            <div>
              <Text strong>Mülk Tipi</Text>
              <Segmented
                block
                style={{ marginTop: 6 }}
                value={mulkTipi}
                onChange={(v) => {
                  setMulkTipi(v);
                  setRedSebep(null);
                }}
                options={['Genel', 'Özel Mülk']}
              />
            </div>
            <div>
              <Text strong>
                Red Sebebi <Text type="danger">*</Text>
              </Text>
              <Select
                style={{ width: '100%', marginTop: 6 }}
                placeholder="Red sebebi seçiniz"
                value={redSebep}
                onChange={setRedSebep}
                options={redSecenekleri}
              />
            </div>
          </>
        )}

        <div>
          <Text strong>
            Açıklama <Text type="danger">*</Text>
          </Text>
          <TextArea
            style={{ marginTop: 6 }}
            rows={3}
            value={aciklama}
            onChange={(e) => setAciklama(e.target.value)}
            placeholder="Açıklama girmek zorunludur"
            maxLength={1000}
            showCount
          />
        </div>

        {aksiyon === 'red' && (
          <Alert
            type="warning"
            showIcon
            message="Red durumunda hem red sebebi seçilmeli hem de açıklama girilmelidir."
          />
        )}
      </Space>
    </Modal>
  );
};

export default YsOnayModal;
